import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldX, ShieldCheck, Trash2, User } from "lucide-react";

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const toggleBlock = async (u) => {
    const blocked = !u.blocked;
    if (!window.confirm(blocked ? t("confirm_block") : t("confirm_unblock"))) return;
    await api.put(`/admin/users/${u.user_id}/block`, null, { params: { blocked } });
    toast.success("OK");
    load();
  };

  const remove = async (u) => {
    if (!window.confirm(t("confirm_delete_user"))) return;
    await api.delete(`/admin/users/${u.user_id}`);
    toast.success("OK");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <h1 className="heading text-4xl font-bold text-white mb-1">{t("admin")}</h1>
        <p className="text-zinc-500 text-sm mb-8">{users.length} {t("users_label")}</p>

        {users.length === 0 ? (
          <p className="text-zinc-500">{t("no_users")}</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} data-testid="admin-user-row" className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-sm bg-black border border-zinc-800 flex items-center justify-center overflow-hidden">
                    {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-zinc-600" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{u.name} <span className="text-zinc-500">· {u.email}</span></p>
                    <p className="text-xs text-zinc-500">
                      <span className="label-caps">{u.role}</span> · {u.band_count} {t("bands_count")} ·{" "}
                      <span className={u.blocked ? "text-red-400" : "text-green-400"}>{u.blocked ? t("blocked_label") : t("active")}</span>
                      {u.mfa_enabled && <span className="text-primary"> · MFA</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {u.role !== "admin" && (
                    <>
                      <button data-testid={`block-${u.user_id}`} onClick={() => toggleBlock(u)}
                        className={`flex items-center gap-1 px-3 py-2 label-caps text-xs rounded-sm border ${u.blocked ? "border-green-900 text-green-400 hover:bg-green-950/40" : "border-amber-900 text-amber-400 hover:bg-amber-950/40"}`}>
                        {u.blocked ? <ShieldCheck size={14} /> : <ShieldX size={14} />} {u.blocked ? t("unblock") : t("block")}
                      </button>
                      <button data-testid={`delete-${u.user_id}`} onClick={() => remove(u)}
                        className="flex items-center gap-1 px-3 py-2 label-caps text-xs rounded-sm border border-red-900 text-red-400 hover:bg-red-950/40">
                        <Trash2 size={14} /> {t("delete_account")}
                      </button>
                    </>
                  )}
                  {u.user_id === user?.user_id && <span className="label-caps text-xs text-zinc-600">(you)</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
